/**
 * <g-editor> - Markdown WYSIWYG Editor
 *
 * A minimalistic markdown editor built on Milkdown. Provides WYSIWYG editing
 * with toolbar and markdown output.
 *
 * Covers CommonMark **and** GitHub-Flavored Markdown: headings, bold/italic,
 * ~~strikethrough~~, `inline code`, fenced code, lists, task lists, quotes,
 * links, images, horizontal rules and tables. Anything the parser understands
 * round-trips through getMarkdown(), including constructs with no toolbar
 * button of their own (nested lists, footnotes, autolinks).
 *
 * @example
 * <g-editor placeholder="Start writing..."></g-editor>
 *
 * @example With initial content
 * <g-editor value="# Hello\n\nStart editing..."></g-editor>
 *
 * @example Get markdown content
 * const editor = document.querySelector('g-editor');
 * const markdown = editor.getMarkdown();
 *
 * @attr {string} placeholder - Placeholder text when empty
 * @attr {string} value - Initial markdown content
 * @attr {boolean} readonly - Make editor read-only
 *
 * @fires change - When content changes, detail: { markdown }
 */
import { Editor, rootCtx, defaultValueCtx, editorViewCtx, serializerCtx, parserCtx } from '@milkdown/kit/core';
import {
  commonmark,
  toggleStrongCommand,
  toggleEmphasisCommand,
  toggleInlineCodeCommand,
  toggleLinkCommand,
  updateLinkCommand,
  linkSchema,
  insertImageCommand,
  insertHrCommand,
  createCodeBlockCommand,
  wrapInHeadingCommand,
  wrapInBulletListCommand,
  wrapInOrderedListCommand,
  wrapInBlockquoteCommand,
} from '@milkdown/kit/preset/commonmark';
import { gfm, toggleStrikethroughCommand, insertTableCommand, tableCellSchema, tableHeaderSchema } from '@milkdown/kit/preset/gfm';
import { history, undoCommand, redoCommand } from '@milkdown/kit/plugin/history';
import { callCommand } from '@milkdown/kit/utils';
import { Slice } from '@milkdown/kit/prose/model';
import { TextSelection } from '@milkdown/kit/prose/state';
import { isInTable, addRowAfter, addColumnAfter, deleteRow, deleteColumn, deleteTable } from '@milkdown/kit/prose/tables';

import { ensureEditorStyles } from './editor-styles.js';

/**
 * Milkdown's link mark is inclusive, so a caret sitting at the end of a link
 * keeps extending it: insert a link, carry on typing, and the rest of the
 * sentence vanishes into the href. prosemirror-schema-basic marks links
 * non-inclusive for exactly this reason. With a Link button on the toolbar,
 * "insert a link, then keep writing" is the common path, so match that.
 */
const nonInclusiveLink = linkSchema.extendSchema((prev) => (ctx) => ({
  ...prev(ctx),
  inclusive: false,
}));

/**
 * An empty table cell holds an empty paragraph, and commonmark's
 * remarkPreserveEmptyLinePlugin serializes an empty paragraph as a literal
 * `<br />` so blank lines between blocks survive a round trip. Sound rule in
 * body text; in a table it means the moment you insert one you have
 * `| <br /> | <br /> |` sitting in your saved markdown. Blank cells are
 * ordinary in a table and mean nothing — emit them as blank.
 */
const isBlankCell = (node) =>
  node.childCount === 1
  && node.firstChild.type.name === 'paragraph'
  && node.firstChild.content.size === 0;

const blankCellsStayBlank = (schema) => schema.extendSchema((prev) => (ctx) => {
  const base = prev(ctx);
  return {
    ...base,
    toMarkdown: {
      ...base.toMarkdown,
      runner: (state, node) => {
        state.openNode('tableCell');
        if (!isBlankCell(node)) state.next(node.content);
        state.closeNode();
      },
    },
  };
});

export class GEditor extends HTMLElement {
  static get observedAttributes() {
    return ['readonly', 'placeholder'];
  }

  constructor() {
    super();
    this._editor = null;
    this._value = '';
    this._prompt = null;
    this._tableBar = null;
    this._promptKind = null;
  }

  connectedCallback() {
    this._value = this.getAttribute('value') || '';
    this.render();
  }

  disconnectedCallback() {
    this.destroy();
  }

  async render() {
    // Tables, images and task boxes have to look like themselves before they
    // can be edited — see ./editor-styles.js for why the bundle owns that.
    ensureEditorStyles();

    // Container styling via Tailwind
    this.classList.add(
      'block', 'rounded-lg', 'border', 'border-base-300',
      'bg-base-100', 'overflow-hidden'
    );

    const toolbar = this._buildToolbar();
    this.appendChild(toolbar);

    // Insert bar for links and images: a URL has to be typed somewhere, and a
    // native prompt() would steal the selection we are about to wrap.
    this._prompt = this._buildPrompt();
    this.appendChild(this._prompt);

    // Row/column controls, revealed only while the caret sits in a table.
    this._tableBar = this._buildTableBar();
    this.appendChild(this._tableBar);

    // Create editor container
    const editorRoot = document.createElement('div');
    editorRoot.className = 'g-editor-root p-4 min-h-[150px] focus-within:outline-none';
    this.appendChild(editorRoot);

    // Initialize Milkdown editor. gfm rides on top of commonmark rather than
    // replacing it: it adds the table / strikethrough / task-list schema plus
    // remark-gfm, so `| a | b |` in the source parses as a table instead of
    // landing in a paragraph as literal pipes.
    this._editor = await Editor.make()
      .config((ctx) => {
        ctx.set(rootCtx, editorRoot);
        ctx.set(defaultValueCtx, this._value);
      })
      .use(commonmark)
      .use(gfm)
      .use(nonInclusiveLink)
      .use(blankCellsStayBlank(tableCellSchema))
      .use(blankCellsStayBlank(tableHeaderSchema))
      .use(history)
      .create();

    // Set readonly if attribute present
    if (this.hasAttribute('readonly')) {
      const view = this._editor.ctx.get(editorViewCtx);
      view.setProps({ editable: () => false });
      toolbar.style.display = 'none';
    }

    // Wire up toolbar buttons
    toolbar.addEventListener('click', (e) => {
      const toggle = e.target.closest('button[data-toolbar-toggle]');
      if (toggle) {
        this._toggleToolbarMore(toggle);
        this._focus();            // keep the caret where the writer left it
        return;
      }

      const btn = e.target.closest('button[data-command]');
      if (!btn) return;

      const cmd = btn.dataset.command;
      this._runCommand(cmd);

      // Refocus editor — unless we just opened the insert bar, which owns focus.
      if (cmd !== 'link' && cmd !== 'image') this._focus();
    });

    this._tableBar.addEventListener('click', (e) => {
      const btn = e.target.closest('button[data-table]');
      if (!btn) return;
      this._runTableCommand(btn.dataset.table);
      this._focus();
    });

    // A task item draws its checkbox as a ::before glyph in the list gutter, so
    // a click landing on the <li> box itself — rather than the paragraph inside
    // it — is a click on the box. Toggle instead of moving the caret.
    editorRoot.addEventListener('mousedown', (e) => {
      if (this.hasAttribute('readonly')) return;
      const li = e.target;
      if (!(li instanceof HTMLElement) || !li.matches('li[data-item-type="task"]')) return;
      e.preventDefault();
      this._toggleChecked(li);
    });

    // Emit change events on editor updates
    const view = this._editor.ctx.get(editorViewCtx);
    const self = this;
    const originalDispatch = view.dispatch.bind(view);
    view.dispatch = function (tr) {
      originalDispatch(tr);
      self._syncTableBar();
      if (tr.docChanged) {
        self.dispatchEvent(new CustomEvent('change', {
          detail: { markdown: self.getMarkdown() },
          bubbles: true
        }));
      }
    };
    this._syncTableBar();
  }

  // ── Chrome ──

  // Nineteen buttons in one wrapping row meant the row wrapped on any narrow
  // editor, and the things you actually reach for while writing — bold, a
  // heading, a list, a link — were lost among the ones you reach for once a
  // month. So the row is split: the writing essentials stay put, everything else
  // moves to a second row behind a toggle.
  //
  // The split is by FREQUENCY, not by category. H1/H2 stay while H3 goes, and
  // undo/redo go despite being common, because Ctrl+Z is muscle memory and a
  // toolbar button is not the way anyone undoes anything.
  _buildToolbar() {
    const btn = (cmd, label, title, extra = '') =>
      `<button type="button" data-command="${cmd}" class="btn btn-xs btn-ghost${extra ? ' ' + extra : ''}" title="${title}">${label}</button>`;
    const sep = '<div class="divider divider-horizontal mx-0"></div>';

    const toolbar = document.createElement('div');
    toolbar.className = 'g-editor-toolbar border-b border-base-300 bg-base-200';
    toolbar.innerHTML = `
      <div class="g-editor-toolbar-main flex flex-wrap items-center gap-1 p-2">
        ${btn('bold', 'B', 'Bold (Ctrl+B)', 'font-bold')}
        ${btn('italic', 'I', 'Italic (Ctrl+I)', 'italic')}
        ${sep}
        ${btn('h1', 'H1', 'Heading 1')}
        ${btn('h2', 'H2', 'Heading 2')}
        ${sep}
        ${btn('bullet', '&bull; List', 'Bullet List')}
        ${btn('ordered', '1. List', 'Numbered List')}
        ${sep}
        ${btn('link', 'Link', 'Insert Link')}
        <button type="button" data-toolbar-toggle class="btn btn-xs btn-ghost ml-auto"
          title="More formatting" aria-label="More formatting" aria-expanded="false">&#9662;</button>
      </div>
      <div data-toolbar-more class="g-editor-toolbar-more hidden flex-wrap items-center gap-1 px-2 pb-2">
        ${btn('strike', 'S', 'Strikethrough', 'line-through')}
        ${btn('code', '&lt;/&gt;', 'Inline code', 'font-mono')}
        ${sep}
        ${btn('h3', 'H3', 'Heading 3')}
        ${sep}
        ${btn('task', '&#9745; Task', 'Task List')}
        ${btn('quote', '&ldquo; Quote', 'Blockquote')}
        ${btn('codeblock', '{ }', 'Code Block', 'font-mono')}
        ${sep}
        ${btn('image', 'Image', 'Insert Image')}
        ${btn('table', 'Table', 'Insert Table')}
        ${btn('hr', '&mdash;', 'Horizontal Rule')}
        ${sep}
        ${btn('undo', '&#8630;', 'Undo (Ctrl+Z)')}
        ${btn('redo', '&#8631;', 'Redo (Ctrl+Y)')}
      </div>`;
    return toolbar;
  }

  // Toggled by CLASS, not the hidden attribute: `.flex` and `[hidden]` carry the
  // same specificity, so a `display:flex` utility on the same element would win
  // and the row would stay visible. Tailwind's own `.hidden` is ordered to beat
  // it — the same reason the notes composer toggles a class.
  //
  // The caret is a text glyph, not a Font Awesome <i>. Every other button in this
  // toolbar is one (B, </>, { }, the undo arrows), and the editor has never
  // needed an icon font — pulling one in for a single chevron would break the
  // toggle into an invisible 0-width button on any host that does not load FA,
  // which is exactly what demo/editor.html does not.
  _toggleToolbarMore(toggle) {
    const more = this.querySelector('[data-toolbar-more]');
    if (!more) return;
    const open = more.classList.contains('hidden');
    more.classList.toggle('hidden', !open);
    more.classList.toggle('flex', open);
    toggle.innerHTML = open ? '&#9652;' : '&#9662;';
    toggle.setAttribute('aria-expanded', String(open));
    toggle.setAttribute('title', open ? 'Fewer options' : 'More formatting');
    toggle.setAttribute('aria-label', open ? 'Fewer options' : 'More formatting');
  }

  // Deliberately a <div>, not a <form>: <g-editor> is routinely dropped inside
  // an app's own <form>, and a nested form is invalid HTML the parser unwinds.
  _buildPrompt() {
    const bar = document.createElement('div');
    bar.className = 'g-editor-prompt flex flex-wrap items-center gap-2 p-2 border-b border-base-300 bg-base-200';
    bar.style.display = 'none';
    bar.innerHTML = [
      '<input data-field="primary" type="text" class="input input-xs input-bordered grow min-w-[12rem]">',
      '<input data-field="secondary" type="text" class="input input-xs input-bordered grow min-w-[9rem]">',
      '<button type="button" data-act="insert" class="btn btn-xs btn-primary">Insert</button>',
      '<button type="button" data-act="cancel" class="btn btn-xs btn-ghost">Cancel</button>',
    ].join('');
    bar.addEventListener('click', (e) => {
      const btn = e.target.closest('button[data-act]');
      if (!btn) return;
      if (btn.dataset.act === 'insert') this._submitPrompt();
      else this._closePrompt();
    });
    bar.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); this._submitPrompt(); }
      else if (e.key === 'Escape') { e.preventDefault(); this._closePrompt(); }
    });
    return bar;
  }

  _buildTableBar() {
    const bar = document.createElement('div');
    bar.className = 'g-editor-tablebar flex flex-wrap items-center gap-1 p-2 border-b border-base-300 bg-base-200';
    bar.style.display = 'none';
    bar.innerHTML = [
      '<span class="text-xs opacity-60 mr-1">Table</span>',
      '<button type="button" data-table="row-after" class="btn btn-xs btn-ghost" title="Add row below">+ Row</button>',
      '<button type="button" data-table="col-after" class="btn btn-xs btn-ghost" title="Add column right">+ Col</button>',
      '<button type="button" data-table="del-row" class="btn btn-xs btn-ghost" title="Delete row">&minus; Row</button>',
      '<button type="button" data-table="del-col" class="btn btn-xs btn-ghost" title="Delete column">&minus; Col</button>',
      '<div class="divider divider-horizontal mx-0"></div>',
      '<button type="button" data-table="align-left" class="btn btn-xs btn-ghost" title="Align column left">&#8676;</button>',
      '<button type="button" data-table="align-center" class="btn btn-xs btn-ghost" title="Align column center">&#8596;</button>',
      '<button type="button" data-table="align-right" class="btn btn-xs btn-ghost" title="Align column right">&#8677;</button>',
      '<div class="divider divider-horizontal mx-0"></div>',
      '<button type="button" data-table="delete" class="btn btn-xs btn-ghost text-error" title="Delete table">Delete table</button>',
    ].join('');
    return bar;
  }

  // ── Commands ──

  _runCommand(cmd) {
    if (!this._editor) return;

    switch (cmd) {
      case 'bold':
        this._editor.action(callCommand(toggleStrongCommand.key));
        break;
      case 'italic':
        this._editor.action(callCommand(toggleEmphasisCommand.key));
        break;
      case 'strike':
        this._editor.action(callCommand(toggleStrikethroughCommand.key));
        break;
      case 'code':
        this._editor.action(callCommand(toggleInlineCodeCommand.key));
        break;
      case 'h1':
        this._editor.action(callCommand(wrapInHeadingCommand.key, 1));
        break;
      case 'h2':
        this._editor.action(callCommand(wrapInHeadingCommand.key, 2));
        break;
      case 'h3':
        this._editor.action(callCommand(wrapInHeadingCommand.key, 3));
        break;
      case 'bullet':
        this._editor.action(callCommand(wrapInBulletListCommand.key));
        break;
      case 'ordered':
        this._editor.action(callCommand(wrapInOrderedListCommand.key));
        break;
      case 'task':
        this._toggleTaskItem();
        break;
      case 'quote':
        this._editor.action(callCommand(wrapInBlockquoteCommand.key));
        break;
      case 'codeblock':
        this._editor.action(callCommand(createCodeBlockCommand.key));
        break;
      case 'hr':
        this._editor.action(callCommand(insertHrCommand.key));
        break;
      case 'table':
        // 3 rows counting the header, so the user gets a header + two body rows.
        this._editor.action(callCommand(insertTableCommand.key, { row: 3, col: 3 }));
        break;
      case 'link':
      case 'image':
        this._openPrompt(cmd);
        break;
      case 'undo':
        this._editor.action(callCommand(undoCommand.key));
        break;
      case 'redo':
        this._editor.action(callCommand(redoCommand.key));
        break;
    }
  }

  _runTableCommand(cmd) {
    const view = this._view();
    if (!view) return;
    const run = (command) => command(view.state, view.dispatch.bind(view));

    switch (cmd) {
      case 'row-after': run(addRowAfter); break;
      case 'col-after': run(addColumnAfter); break;
      case 'del-row': run(deleteRow); break;
      case 'del-col': run(deleteColumn); break;
      case 'delete': run(deleteTable); break;
      case 'align-left': this._setColumnAlign('left'); break;
      case 'align-center': this._setColumnAlign('center'); break;
      case 'align-right': this._setColumnAlign('right'); break;
    }
  }

  /**
   * Alignment is a per-COLUMN property of a markdown table, and gfm's
   * keepTableAlignPlugin copies the header cell's `alignment` down over every
   * body cell after each change. Setting it on the cell under the caret would
   * therefore be reverted a tick later, so write it to the header cell of that
   * column and let the plugin propagate.
   */
  _setColumnAlign(alignment) {
    const view = this._view();
    if (!view) return;
    const $pos = view.state.selection.$from;

    let cellDepth = null;
    for (let d = $pos.depth; d > 0; d--) {
      const name = $pos.node(d).type.name;
      if (name === 'table_cell' || name === 'table_header') { cellDepth = d; break; }
    }
    if (cellDepth == null || cellDepth < 2) return;

    const column = $pos.index(cellDepth - 1);
    const table = $pos.node(cellDepth - 2);
    const headerRow = table.firstChild;
    if (!headerRow || column >= headerRow.childCount) return;

    // +1 enters the table, +1 enters its header row.
    let cellPos = $pos.before(cellDepth - 2) + 2;
    for (let i = 0; i < column; i++) cellPos += headerRow.child(i).nodeSize;

    const header = headerRow.child(column);
    view.dispatch(view.state.tr.setNodeMarkup(cellPos, undefined, { ...header.attrs, alignment }));
  }

  /**
   * Turn the list item under the caret into a task item, and back. A task item
   * is an ordinary list item whose `checked` attr is a boolean rather than
   * null — that is the whole difference, and it is what makes it serialize as
   * `- [ ]` instead of `- `.
   */
  _toggleTaskItem() {
    const view = this._view();
    if (!view) return;
    if (!this._findListItem(view.state)) {
      this._editor.action(callCommand(wrapInBulletListCommand.key));
    }
    const item = this._findListItem(view.state);
    if (!item) return;
    const checked = item.node.attrs.checked == null ? false : null;
    view.dispatch(view.state.tr.setNodeMarkup(item.pos, undefined, { ...item.node.attrs, checked }));
  }

  /** Tick / untick the task item whose gutter box was clicked. */
  _toggleChecked(li) {
    const view = this._view();
    if (!view) return;
    const $pos = view.state.doc.resolve(view.posAtDOM(li, 0));
    for (let d = $pos.depth; d > 0; d--) {
      const node = $pos.node(d);
      if (node.type.name !== 'list_item' || node.attrs.checked == null) continue;
      view.dispatch(view.state.tr.setNodeMarkup($pos.before(d), undefined, {
        ...node.attrs,
        checked: !node.attrs.checked,
      }));
      return;
    }
  }

  _findListItem(state) {
    const $pos = state.selection.$from;
    for (let d = $pos.depth; d > 0; d--) {
      const node = $pos.node(d);
      if (node.type.name === 'list_item') return { node, pos: $pos.before(d) };
    }
    return null;
  }

  // ── Link / image insert bar ──

  _openPrompt(kind) {
    const view = this._view();
    if (!view) return;
    this._promptKind = kind;

    const { from, to } = view.state.selection;
    const selected = view.state.doc.textBetween(from, to, ' ').trim();
    const [primary, secondary] = this._promptFields();

    if (kind === 'link') {
      primary.placeholder = 'https://example.com';
      primary.value = '';
      secondary.placeholder = 'Link text';
      secondary.value = selected;
      // With text already selected the label is settled — only the URL is open.
      secondary.classList.toggle('hidden', Boolean(selected));
    } else {
      primary.placeholder = 'https://example.com/image.png';
      primary.value = '';
      secondary.placeholder = 'Alt text (optional)';
      secondary.value = selected;
      secondary.classList.remove('hidden');
    }

    this._prompt.style.display = 'flex';
    primary.focus();
  }

  _closePrompt() {
    this._promptKind = null;
    this._prompt.style.display = 'none';
    this._focus();
  }

  _submitPrompt() {
    const view = this._view();
    const kind = this._promptKind;
    if (!view || !kind) return;
    const [primary, secondary] = this._promptFields();
    const url = primary.value.trim();
    if (!url) { this._closePrompt(); return; }

    if (kind === 'image') {
      this._editor.action(callCommand(insertImageCommand.key, { src: url, alt: secondary.value.trim() }));
    } else {
      this._insertLink(url, secondary.value.trim());
    }
    this._closePrompt();
  }

  /**
   * Three cases, because toggleLinkCommand is toggleMark and toggling is the
   * wrong verb for two of them:
   *
   *  - selection already linked → retarget it, don't strip the link off
   *  - collapsed caret          → write the label and mark it in one go. Going
   *    via insertText + toggle would inherit the marks either side of the
   *    caret, so a caret parked after an existing link produced linked text
   *    that toggleMark then promptly UNlinked.
   *  - plain selection          → the ordinary toggle
   */
  _insertLink(href, label) {
    const view = this._view();
    const { state } = view;
    const linkType = state.schema.marks.link;
    const { from, to, empty } = state.selection;

    if (linkType && !empty && state.doc.rangeHasMark(from, to, linkType)) {
      this._editor.action(callCommand(updateLinkCommand.key, { href }));
      return;
    }

    if (empty) {
      const text = label || href;
      const node = state.schema.text(text, linkType ? [linkType.create({ href })] : undefined);
      const tr = state.tr.replaceWith(from, to, node);
      // Park the caret after the link with no marks carried, so the next thing
      // typed is not swallowed into it.
      tr.setSelection(TextSelection.create(tr.doc, from + node.nodeSize)).setStoredMarks([]);
      view.dispatch(tr);
      return;
    }

    this._editor.action(callCommand(toggleLinkCommand.key, { href }));
  }

  _promptFields() {
    return [
      this._prompt.querySelector('[data-field="primary"]'),
      this._prompt.querySelector('[data-field="secondary"]'),
    ];
  }

  // ── Helpers ──

  _view() {
    return this._editor ? this._editor.ctx.get(editorViewCtx) : null;
  }

  _focus() {
    const view = this._view();
    if (view) view.focus();
  }

  _syncTableBar() {
    if (!this._tableBar || this.hasAttribute('readonly')) return;
    const view = this._view();
    const inTable = Boolean(view && isInTable(view.state));
    this._tableBar.style.display = inTable ? 'flex' : 'none';
  }

  destroy() {
    if (this._editor) {
      this._editor.destroy();
      this._editor = null;
    }
  }

  /**
   * Get the current markdown content
   * @returns {string} Markdown string
   */
  getMarkdown() {
    if (!this._editor) return '';
    const view = this._editor.ctx.get(editorViewCtx);
    const serializer = this._editor.ctx.get(serializerCtx);
    return serializer(view.state.doc);
  }

  /**
   * Set the markdown content
   * @param {string} markdown - Markdown string to set
   */
  setMarkdown(markdown) {
    if (!this._editor) {
      this._value = markdown;
      return;
    }

    // A write-back hands us the markdown we just emitted — an Alpine x-effect
    // bound to the same property, a re-render, a socket refresh. Replacing the
    // doc with its own content is never a no-op: it resets the caret, and the
    // parse drops a trailing space that the serializer had preserved, so the
    // space you are in the middle of typing disappears. Skip the round-trip
    // when nothing actually changed.
    if (markdown === this.getMarkdown()) return;

    const view = this._editor.ctx.get(editorViewCtx);
    const parser = this._editor.ctx.get(parserCtx);
    const doc = parser(markdown);
    const tr = view.state.tr.replace(0, view.state.doc.content.size, new Slice(doc.content, 0, 0));
    view.dispatch(tr);
  }

  /**
   * Get/set the value property (alias for markdown)
   */
  get value() {
    return this.getMarkdown();
  }

  set value(markdown) {
    this.setMarkdown(markdown);
  }
}

if (!customElements.get('g-editor')) {
  customElements.define('g-editor', GEditor);
}
